import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';

import {NodeSpec} from '../execution/graph';
import {Source} from '../execution/pipes';
import {ArrayValue, PlatedMaterialValue} from '../types/data';

import {MixingTransformer} from './mixing_transformer';
import {FlattenMixToArrayTransformer} from './mix_product_node';
import {makeWaterPlatedMaterial} from './sources';

export const NODE = {
  id: 'node_mix_zipper',
  label: 'Mix zipper',
  inputs: [
    {
      id: 'materials',
      label: 'Materials',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
    {
      id: 'prefix',
      label: 'Prefix',
      type: {kind: 'string'},
    },
    {
      id: 'mix',
      label: 'Mixes',
      type: {
        kind: 'table',
        columnLabels: [],
        columnTypes: [],
        defaults: [],
        rowCountFixed: true,
      },
    },
    {
      id: 'output_plate',
      label: 'Plate',
      type: {
        kind: 'plate',
        contents: undefined,
      },
    },
    {
      id: 'remainder',
      label: 'Remainder',
      type: {
        kind: 'plated_material',
        material: undefined,
      },
    },
  ],
  outputs: [
    {
      id: 'materials',
      label: 'Materials',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
  ],
  pipe: ({node, overlay}) => {
    const materials = overlay.padToSources(
      checkExists(node.inputs.get('materials')),
    ) as Array<Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>>;
    const prefix = overlay.singleValuePadSource(
      checkExists(node.inputs.get('prefix')),
      'string',
      () => ({kind: 'string', value: 'product'}),
    );
    const mix = overlay.singleValuePadSource(
      checkExists(node.inputs.get('mix')),
      'table',
      () => ({
        kind: 'table',
        values: new Map([]),
      }),
    );
    const plate = overlay.singleValuePadSource(
      checkExists(node.inputs.get('output_plate')),
      'plate',
      context => ({
        kind: 'plate',
        id: context.lab.presetPlates.fourCelsius,
        wells: new Map(),
      }),
    );
    const remainder = overlay.singleValuePadSource(
      checkExists(node.inputs.get('remainder')),
      'plated_material',
      context => makeWaterPlatedMaterial(context),
    );

    const mixes = new FlattenMixToArrayTransformer(
      overlay.nextIdFor(node),
      zipper,
      materials,
      mix,
      prefix,
    );
    const mixing = new MixingTransformer(
      overlay.nextIdFor(node),
      materials,
      mixes,
      plate,
      remainder,
    );
    overlay.setPadSource(checkExists(node.outputs.get('materials')), mixing);
  },
} as const satisfies NodeSpec;

export function* zipper<V>(
  groups: V[][],
): Generator<number[], undefined, undefined> {
  const head = groups[0];
  for (const group of groups) {
    if (head.length !== group.length) {
      throw new IncompatibleMaterialsError(
        'All material inputs must have the same length to zipper',
      );
    }
  }

  for (let i = 0; i < head.length; ++i) {
    yield Array(groups.length).fill(i);
  }
}

export class IncompatibleMaterialsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = this.constructor.name;
  }
}
